import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Patterns to detect chunk/dynamic import loading errors
 * These errors occur when a new version is deployed and users
 * still have the old app cached with references to old chunk files
 */
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/i,
  /dynamically imported module/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

const RELOAD_STORAGE_KEY = 'chunk_error_reload_attempted';
const RELOAD_TIMESTAMP_KEY = 'chunk_error_reload_timestamp';
const RELOAD_COOLDOWN_MS = 10000; // 10 seconds cooldown between reload attempts

/**
 * Custom error handler that catches chunk loading failures and reloads the page.
 * This handles the common issue where users have cached old chunks after a deployment.
 * 
 * Wraps Sentry's error handler to maintain error reporting functionality.
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  private sentryHandler: ErrorHandler;

  constructor() {
    this.sentryHandler = Sentry.createErrorHandler({
      showDialog: false,
    });
  }

  handleError(error: any): void {
    const errorMessage = this.extractErrorMessage(error);
    
    if (this.isChunkLoadError(errorMessage)) {
      // Don't send chunk errors to Sentry - they're expected after deployments
      console.warn('[ChunkErrorHandler] Detected chunk loading error:', errorMessage);
      
      if (this.shouldReload()) {
        console.log('[ChunkErrorHandler] Reloading page to fetch latest version...');
        this.markReloadAttempt();
        
        // Clear any service worker caches if present
        this.clearCaches().then(() => {
          window.location.reload();
        });
      } else {
        console.warn('[ChunkErrorHandler] Reload already attempted recently, skipping to prevent loop');
        // Clear the flag after cooldown period so future chunk errors can trigger reload
        this.scheduleReloadFlagClear();
      }
      
      // Don't forward chunk errors to Sentry
      return;
    }

    // Forward only real errors to Sentry
    this.sentryHandler.handleError(error);
  }

  /**
   * Extract error message from various error formats
   */
  private extractErrorMessage(error: any): string {
    if (!error) return '';
    
    // Handle Error objects
    if (error instanceof Error) {
      return error.message || error.toString();
    }
    
    // Handle rejection reasons
    if (error.rejection) {
      return this.extractErrorMessage(error.rejection);
    }
    
    // Handle nested error property
    if (error.error) {
      return this.extractErrorMessage(error.error);
    }
    
    // Handle string errors
    if (typeof error === 'string') {
      return error;
    }
    
    // Handle objects with message property
    if (error.message) {
      return error.message;
    }
    
    return error.toString?.() || '';
  }

  /**
   * Check if the error message matches any chunk loading error patterns
   */
  private isChunkLoadError(message: string): boolean {
    if (!message) return false;
    return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
  }

  /**
   * Check if we should reload based on recent reload attempts
   * Prevents infinite reload loops
   */
  private shouldReload(): boolean {
    try {
      const lastReloadTimestamp = sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);
      
      if (lastReloadTimestamp) {
        const timeSinceLastReload = Date.now() - parseInt(lastReloadTimestamp, 10);
        
        // If we reloaded recently, don't reload again
        if (timeSinceLastReload < RELOAD_COOLDOWN_MS) {
          return false;
        }
      }
      
      return true;
    } catch {
      // If sessionStorage is unavailable, allow reload but be cautious
      return true;
    }
  }

  /**
   * Mark that we're attempting a reload to prevent loops
   */
  private markReloadAttempt(): void {
    try {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, 'true');
      sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, Date.now().toString());
    } catch {
      // Ignore sessionStorage errors
    }
  }

  /**
   * Schedule clearing the reload flag after the cooldown period
   */
  private scheduleReloadFlagClear(): void {
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_STORAGE_KEY);
        sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
      } catch {
        // Ignore sessionStorage errors
      }
    }, RELOAD_COOLDOWN_MS);
  }

  /**
   * Clear any caches that might be holding old chunks
   */
  private async clearCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('[ChunkErrorHandler] Cleared browser caches');
      }
    } catch (e) {
      console.warn('[ChunkErrorHandler] Failed to clear caches:', e);
    }
  }
}

